import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatListBulleted,
  FormatListNumbered,
} from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import { toast } from "react-hot-toast";
import useRectorCommentService from "../../api/services/RectorCommentService";
import * as yup from "yup";

// Custom Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, maxLength = 10000000, textAreaId = 'rich-text-area' }) => {
  const [formats, setFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    align: 'left',
    list: 'none'
  });

  const handleFormatChange = (format, value) => {
    setFormats(prev => ({ ...prev, [format]: value }));
    
    // Apply formatting to selected text
    const textarea = document.getElementById(textAreaId);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = textarea.value.substring(start, end);
      
      if (selectedText) {
        let formattedText = selectedText;
        
        if (format === 'bold') {
          formattedText = value ? `<b>${selectedText}</b>` : selectedText.replace(/<\/?b>/g, '');
        } else if (format === 'italic') {
          formattedText = value ? `<i>${selectedText}</i>` : selectedText.replace(/<\/?i>/g, '');
        } else if (format === 'underline') {
          formattedText = value ? `<u>${selectedText}</u>` : selectedText.replace(/<\/?u>/g, '');
        } else if (format === 'strikethrough') {
          formattedText = value ? `<s>${selectedText}</s>` : selectedText.replace(/<\/?s>/g, '');
        }
        
        const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
        onChange(newValue);
      }
    }
  };

  const handleListFormat = (listType) => {
    const textarea = document.getElementById(textAreaId);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = textarea.value.substring(start, end);
      
      if (selectedText) {
        let formattedText = selectedText;
        const lines = selectedText.split('\n');
        
        if (listType === 'bullet') {
          formattedText = lines.map(line => line.trim() ? `• ${line}` : line).join('\n');
        } else if (listType === 'number') {
          formattedText = lines.map((line, index) => line.trim() ? `${index + 1}. ${line}` : line).join('\n');
        }
        
        const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
        onChange(newValue);
      }
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Paper elevation={1} sx={{ p: 1, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" value={formats.bold} exclusive onChange={(e, value) => handleFormatChange('bold', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Bold">
              <FormatBold />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <ToggleButtonGroup size="small" value={formats.italic} exclusive onChange={(e, value) => handleFormatChange('italic', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Italic">
              <FormatItalic />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <ToggleButtonGroup size="small" value={formats.underline} exclusive onChange={(e, value) => handleFormatChange('underline', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Underline">
              <FormatUnderlined />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <ToggleButtonGroup size="small" value={formats.strikethrough} exclusive onChange={(e, value) => handleFormatChange('strikethrough', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Strikethrough">
              <FormatStrikethrough />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <Box sx={{ borderLeft: '1px solid #ccc', height: '24px', mx: 1 }} />
        
        <ToggleButtonGroup size="small" value={formats.align} exclusive onChange={(e, value) => setFormats(prev => ({ ...prev, align: value }))}>
          <ToggleButton value="left">
            <Tooltip title="Align Left">
              <FormatAlignLeft />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="center">
            <Tooltip title="Align Center">
              <FormatAlignCenter />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="right">
            <Tooltip title="Align Right">
              <FormatAlignRight />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <Box sx={{ borderLeft: '1px solid #ccc', height: '24px', mx: 1 }} />
        
        <IconButton size="small" onClick={() => handleListFormat('bullet')}>
          <Tooltip title="Bullet List">
            <FormatListBulleted />
          </Tooltip>
        </IconButton>
        
        <IconButton size="small" onClick={() => handleListFormat('number')}>
          <Tooltip title="Numbered List">
            <FormatListNumbered />
          </Tooltip>
        </IconButton>
      </Paper>
      
      {/* Text Area */}
      <TextField
        id={textAreaId}
        fullWidth
        multiline
        rows={8}
        value={value}
        onChange={(e) => {
          const inputValue = e.target.value;
          // Strip HTML tags to count actual text content
          const textContent = inputValue.replace(/<[^>]*>/g, '');
          if (textContent.length <= maxLength) {
            onChange(inputValue);
          } else {
            toast.warning("Text content exceeds character limit");
          }
        }}
        placeholder={placeholder}
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            fontFamily: 'monospace',
            fontSize: '14px',
            lineHeight: '1.5',
          }
        }}
      />
    </Box>
  );
};

// Validation schema for Rector Message Dialog
const CreateEvaluationSchema = yup.object().shape({
  studentId: yup.string().required("Student is required"),
  termId: yup.string().required("Term is required"),
  rectorComments: yup.string().test(
    'max-length',
    'Rector comments must be 10,000,000 characters or less',
    function(value) {
      if (!value) return true; // Allow empty values
      // Strip HTML tags to count actual text content
      const textContent = value.replace(/<[^>]*>/g, '');
      return textContent.length <= 10000000;
    }
  ),
});

const getInitialValues = (selectedStudent, existingData = null, activeTerms = []) => ({
  studentId: selectedStudent?.id || "",
  termId: existingData?.termId || activeTerms[0]?.id || "",
  rectorComments: existingData?.rectorComments || "",
});

const RectorCommentsDialog = ({ open, onClose, onSuccess, students, terms, selectedStudent }) => {
  const { 
    createRectorCommentByTermStudent, 
    getRectorCommentByTermStudent, 
    updatePartialRectorCommentByTermStudent,
    createRectorComment 
  } = useRectorCommentService();
  const [existingData, setExistingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const activeTerms = useMemo(
    () => (terms || []).filter((term) => term.status === "ACTIVE"),
    [terms]
  );

  // Load existing data when dialog opens
  useEffect(() => {
    const loadExistingData = async () => {
      if (open && selectedStudent && activeTerms.length > 0) {
        setIsLoading(true);
        try {
          const defaultTerm = activeTerms[0];
          if (defaultTerm) {
            try {
              const data = await getRectorCommentByTermStudent(defaultTerm.id, selectedStudent.id);
              setExistingData(data);
            } catch (error) {
              setExistingData(null);
            }
          }
        } catch (error) {
          setExistingData(null);
        } finally {
          setIsLoading(false);
        }
      } else if (open) {
        setExistingData(null);
      }
    };

    loadExistingData();
  }, [open, selectedStudent?.id, activeTerms]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setIsLoading(true);
      
      // Prepare DTO with required fields
      const dto = {
        studentId: values.studentId,
        termId: values.termId,
      };
      
      // Add optional fields - always include them, even if empty
      dto.rectorComments = values.rectorComments || null;

      // Check if comment already exists
      const exists = existingData !== null;
      
      if (exists) {
        // Update existing comment using partial update
        await updatePartialRectorCommentByTermStudent(
          values.termId, 
          values.studentId, 
          dto, 
          () => {
            toast.success("Rector's message updated successfully");
            onSuccess?.();
            onClose();
          }
        );
      } else {
        // Create new comment
        await createRectorCommentByTermStudent(dto, () => {
          toast.success("Rector's message saved successfully");
          onSuccess?.();
          onClose();
        });
      }
    } catch (error) {
      console.error("Error saving rector's message:", error);
      const errorMessage = error.response?.data?.message || error.message || "Unknown error";
      
      if (errorMessage.includes("Data truncation") || errorMessage.includes("column size")) {
        toast.error("Database column size issue. Please contact administrator.");
      } else if (error.response?.status === 404) {
        // If not found, try to create
        try {
          const dto = {
            studentId: values.studentId,
            termId: values.termId,
            rectorComments: values.rectorComments || null,
          };
          await createRectorCommentByTermStudent(dto, () => {
            toast.success("Rector's message saved successfully");
            onSuccess?.();
            onClose();
          });
        } catch (createError) {
          toast.error("Failed to save rector's message: " + (createError.response?.data?.message || createError.message || "Unknown error"));
        }
      } else {
        toast.error("Failed to save rector's message: " + errorMessage);
      }
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>RECTOR'S MESSAGE</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={getInitialValues(selectedStudent, existingData, activeTerms)}
          validationSchema={CreateEvaluationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <Grid container spacing={2} mt={1}>
                {/* Student dropdown */}
                <Grid item xs={12} lg={6}>
                  <Field
                    as={TextField}
                    select
                    name="studentId"
                    label="Student"
                    fullWidth
                    variant="filled"
                    value={values.studentId}
                    error={touched.studentId && !!errors.studentId}
                    helperText={touched.studentId && errors.studentId}
                  >
                    {students.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.studentName}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                {/* Term dropdown */}
                <Grid item xs={12} lg={6}>
                  <Field
                    as={TextField}
                    select
                    name="termId"
                    label="Term"
                    fullWidth
                    variant="filled"
                    value={values.termId || activeTerms[0]?.id || ""}
                    error={touched.termId && !!errors.termId}
                    helperText={
                      touched.termId && errors.termId
                        ? errors.termId
                        : activeTerms.length === 0
                          ? "No active terms available"
                          : ""
                    }
                    onChange={async (e) => {
                      const newTermId = e.target.value;
                      setFieldValue("termId", newTermId);
                      if (!selectedStudent?.id || !newTermId) return;
                      setIsLoading(true);
                      try {
                        const data = await getRectorCommentByTermStudent(newTermId, selectedStudent.id);
                        setExistingData(data);
                        setFieldValue("rectorComments", data?.rectorComments || "");
                      } catch {
                        setExistingData(null);
                        setFieldValue("rectorComments", "");
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={activeTerms.length === 0}
                  >
                    {activeTerms.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.termName}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                {/* Rector comments */}
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Rector Comments
                    </Typography>
                    <RichTextEditor
                      value={values.rectorComments || ''}
                      onChange={(content) => setFieldValue("rectorComments", content)}
                      placeholder="Enter rector's comments about the student... (Max 10,000,000 characters)"
                      maxLength={10000000}
                      textAreaId="rector-comments-area"
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        variant="caption" 
                        color={touched.rectorComments && errors.rectorComments ? 'error' : 'text.secondary'}
                      >
                        {touched.rectorComments && errors.rectorComments 
                          ? errors.rectorComments 
                          : (() => {
                              const textContent = (values.rectorComments || '').replace(/<[^>]*>/g, '');
                              const length = textContent.length;
                              const remaining = 10000000 - length;
                              if (remaining < 100000) {
                                return `⚠️ ${length.toLocaleString()}/10,000,000 characters (${remaining.toLocaleString()} remaining)`;
                              }
                              return `${length.toLocaleString()}/10,000,000 characters`;
                            })()
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose} color="secondary" disabled={isSubmitting || isLoading}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting || isLoading}>
                  {isSubmitting || isLoading ? "Saving..." : existingData ? "Update" : "Save"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default RectorCommentsDialog;

